import { ReactNode } from 'react';

import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

type Props<TData> = {
    title: string;
    data?: TData[];
    columns: ColumnDef<TData>[];
    children?: ReactNode;
    className?: string;
};

// Stable reference for the empty/loading case. react-table v8 requires `data` to keep a
// stable identity across renders — passing a fresh `[]` (e.g. `data ?? []`) every render
// makes it treat the data as "changed" each time, which triggers an internal state reset
// → re-render → new array → an infinite re-render loop that freezes the tab while data loads.
const EMPTY_ROWS: never[] = [];

export default function StatisticTable<TData>({ title, data, columns, children, className }: Props<TData>) {
    // TODO: Add loading and error logic
    const table = useReactTable({
        data: data ?? EMPTY_ROWS,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <table className={className}>
            <caption className="pb-2 text-start text-sm font-semibold text-gray-600">{title}</caption>

            <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            const { minSize, maxSize } = header.column.columnDef;
                            return (
                                <th
                                    scope="col"
                                    key={header.id}
                                    className="px-4 py-2 text-start text-xs font-semibold"
                                    style={{
                                        ...(minSize && { minWidth: minSize }),
                                        ...(maxSize && { maxWidth: maxSize }),
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            );
                        })}
                    </tr>
                ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map((row, index) => {
                    return (
                        <tr key={row.id} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                            {row.getVisibleCells().map((cell, cellIndex) => {
                                const { minSize, maxSize } = cell.column.columnDef;
                                const Tag = cellIndex === 0 ? 'th' : 'td';
                                return (
                                    <Tag
                                        {...(cellIndex === 0 && { scope: 'row' })}
                                        key={cell.id}
                                        className="h-10 px-4 py-2 text-start text-xs font-normal"
                                        style={{
                                            ...(minSize && { minWidth: minSize }),
                                            ...(maxSize && { maxWidth: maxSize }),
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Tag>
                                );
                            })}
                        </tr>
                    );
                })}
                {table.getRowModel().rows.length === 0 && (
                    <tr className="h-10 bg-gray-50 px-4 py-2 text-start text-xs font-normal">
                        {table.getAllColumns().map((_, index) => {
                            return <th key={index} className="h-10 px-4 py-2 text-start text-xs font-normal" />;
                        })}
                    </tr>
                )}
                {children}
            </tbody>
        </table>
    );
}
